import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { FlaskRound as Flask, TestTube, Package } from 'lucide-react';
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import useTranslation from "@/hooks/useTranslation";

const Home = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  
  // Navigate to the correct services page based on language
  const navigateToServices = () => {
    if (language === 'es') {
      navigate('/es/servicios');
    } else {
      navigate('/services');
    }
  };
  
  // Get the relative image path based on the current language
  const getRelativeImagePath = (imageName) => {
    // For Spanish paths, we need to go up one directory level
    if (language === 'es') {
      return `../images/${imageName}`;
    }
    // For English paths, use a relative path
    return `images/${imageName}`;
  };
  
  return (
    <div className="min-h-screen">
      <SEO 
        title={`NuWav Laboratories - ${t('heroTitle')}`}
        description={t('heroSubtitle')}
        keywords="medical testing, laboratory services, blood panels, HIPAA compliant, CAP accredited, CLIA certified, diagnostic testing, NuWav Laboratories"
        canonicalUrl="https://nuwavlabs.com/"
      />
      
      <StructuredData 
        type="MedicalBusiness"
        data={{
          '@id': 'https://nuwavlabs.com',
          'medicalSpecialty': 'Medical Laboratory',
          'slogan': t('heroTitle'),
          'image': getRelativeImagePath('home-hero.jpg')
        }}
      />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-[#40E0D0] to-[#000080] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getRelativeImagePath('home-hero.jpg')}
            alt="Laboratory test tubes"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {t('heroTitle')}
            </motion.h1>
            <motion.p 
              className="text-xl mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {t('heroSubtitle')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <Button 
                onClick={navigateToServices}
                size="lg"
                className="bg-white text-[#000080] hover:bg-gray-100"
              >
                {t('exploreServices')}
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t('servicesTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <ServiceCard
              icon={<Flask className="w-12 h-12 text-[#40E0D0]" />}
              title={t('coreLab')}
              description={t('coreLabDesc')}
            />
            <ServiceCard
              icon={<TestTube className="w-12 h-12 text-[#40E0D0]" />}
              title={t('molecularTesting')}
              description={t('molecularTestingDesc')}
            />
            <ServiceCard
              icon={<Package className="w-12 h-12 text-[#40E0D0]" />}
              title={t('reagentsTitle')}
              description={t('reagentsDesc')}
            />
          </div>
        </div>
      </section>

      {/* Added text section above footer */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xl text-gray-600 mb-6">
              {t('homeContent1')}
            </p>
            <p className="text-xl text-gray-600 mb-6">
              {t('homeContent2')}
            </p>
            <p className="text-xl text-gray-600">
              {t('homeContent3')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServiceCard = ({ icon, title, description }) => (
  <motion.div 
    className="p-6 bg-white rounded-lg shadow-lg text-center"
    whileHover={{ scale: 1.05 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <div className="flex justify-center mb-4">{icon}</div>
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </motion.div>
);

export default Home;
